/**
 * O gatilho de troca de contexto no cabeçalho — T3, T18 e V2 da spec #408.
 *
 * Ele mostra onde a pessoa está e abre a lista; quem lista é o ContextSwitcherModal. Era um
 * menu suspenso, e deixou de ser por escala: quem opera a Plataforma alcança todo cliente
 * dela, e centenas de empresas e clínicas juntas não cabem num dropdown do cabeçalho.
 *
 * Aparece só para quem alcança mais de um contexto, e a troca não recarrega a página — grava
 * a escolha no servidor e pede o manifesto de novo, que é o que refaz menu, guardas e módulos.
 */

import React, { useState } from 'react';
import { Button, CircularProgress, Typography } from '@mui/material';
import {
  Business as BusinessIcon,
  MedicalServices as MedicalServicesIcon,
  Public as PublicIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';

import { useManifesto } from '../../stores/manifestoStore';
import type { Perspectiva } from '../../types/manifesto';
import ContextSwitcherModal from './ContextSwitcherModal';

const ICONE_DO_TIPO: Record<Perspectiva, typeof BusinessIcon> = {
  empresa: BusinessIcon,
  prestador: MedicalServicesIcon,
  plataforma: PublicIcon,
};

const BOTAO_SX = {
  maxWidth: 320,
  textTransform: 'none',
  color: 'white',
  borderColor: 'rgba(255, 255, 255, 0.3)',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: 'rgba(255, 255, 255, 0.18)',
  },
} as const;

const CompanySelector: React.FC = () => {
  const { manifesto, isLoading } = useManifesto();
  const [aberto, setAberto] = useState(false);

  const contextos = manifesto?.contextos ?? [];

  // §7 — quem alcança um contexto só nunca vê o gatilho e nunca precisa saber que ele existe.
  if (!manifesto || contextos.length <= 1) {
    return null;
  }

  const ativo = contextos.find(
    (contexto) =>
      contexto.tipo === manifesto.contextoAtivo.tipo && contexto.id === manifesto.contextoAtivo.id
  );
  const IconeAtivo = ICONE_DO_TIPO[ativo?.tipo ?? 'empresa'];
  const nomeAtivo = ativo?.nome ?? 'Escolha o contexto';

  return (
    <>
      <Button
        variant='outlined'
        size='small'
        onClick={() => setAberto(true)}
        disabled={isLoading}
        aria-haspopup='dialog'
        aria-label={`Trocar de contexto — atual: ${nomeAtivo}`}
        startIcon={
          isLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <IconeAtivo />
        }
        endIcon={<UnfoldMoreIcon fontSize='small' />}
        sx={BOTAO_SX}
      >
        <Typography variant='body2' noWrap>
          {nomeAtivo}
        </Typography>
      </Button>

      <ContextSwitcherModal aberto={aberto} aoFechar={() => setAberto(false)} />
    </>
  );
};

export default CompanySelector;
